import React, { useState } from 'react';
import { Plus, Search, Edit2, Trash2, HelpCircle, CheckCircle2, ListFilter } from 'lucide-react';
import { useQuestions, Question, QuestionType, QuestionScope } from './QuestionsContext';
import { catalogItems } from './MenusContext';
import { Toggle } from './Toggle';
export function QuestionsPanel() {
  const {
    questions,
    addQuestion,
    updateQuestion,
    deleteQuestion
  } = useQuestions();
  const [search, setSearch] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const filtered = questions.filter(q => q.question.toLowerCase().includes(search.toLowerCase()));
  const handleSave = (q: Omit<Question, 'id'>, id?: string) => {
    if (id) {
      updateQuestion(id, q);
      setEditingId(null);
    } else {
      addQuestion(q);
      setIsAdding(false);
    }
  };
  return <div className="space-y-6" data-id="element-2882">
      <div className="flex items-center justify-between" data-id="element-2883">
        <div data-id="element-2884">
          <h2 className="text-xl font-bold text-gray-900" data-id="element-2885">Custom Questions</h2>
          <p className="text-sm text-gray-500 mt-1" data-id="element-2886">
            Ask customers questions during checkout.
          </p>
        </div>
        <button onClick={() => setIsAdding(true)} className="bg-orange-500 text-white px-4 py-2 rounded-xl text-sm font-bold hover:bg-orange-600 transition-colors flex items-center gap-2" data-id="element-2887">
          <Plus size={16} data-id="element-2888" />
          Add Question
        </button>
      </div>

      <div className="relative" data-id="element-2889">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} data-id="element-2890" />
        <input type="text" placeholder="Search questions..." value={search} onChange={e => setSearch(e.target.value)} className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 transition-all" data-id="element-2891" />
      </div>

      {isAdding && <QuestionEditor onSave={q => handleSave(q)} onCancel={() => setIsAdding(false)} data-id="element-2892" />}

      <div className="space-y-3" data-id="element-2893">
        {filtered.map(q => editingId === q.id ? <QuestionEditor key={q.id} initial={q} onSave={updated => handleSave(updated, q.id)} onCancel={() => setEditingId(null)} data-id="element-2894" /> : <div key={q.id} className={`bg-white border rounded-xl p-4 transition-all ${q.active ? 'border-gray-200' : 'border-gray-200 bg-gray-50 opacity-60'}`} data-id="element-2895">
              <div className="flex items-start justify-between gap-4" data-id="element-2896">
                <div className="flex-1 min-w-0" data-id="element-2897">
                  <div className="flex items-center gap-2 mb-1" data-id="element-2898">
                    <h3 className="font-bold text-gray-900" data-id="element-2899">{q.question}</h3>
                    {q.required && <span className="px-1.5 py-0.5 rounded bg-rose-50 text-rose-600 text-[10px] font-bold uppercase tracking-wider" data-id="element-2900">
                        Required
                      </span>}
                  </div>
                  {q.helpText && <p className="text-sm text-gray-500 mb-2" data-id="element-2901">{q.helpText}</p>}
                  <div className="flex items-center gap-3 text-xs text-gray-500" data-id="element-2902">
                    <span className="flex items-center gap-1" data-id="element-2903">
                      <HelpCircle size={12} data-id="element-2904" />
                      {q.type === 'text' ? 'Text Input' : q.type === 'single-select' ? 'Single Choice' : 'Multiple Choice'}
                    </span>
                    <span className="flex items-center gap-1" data-id="element-2905">
                      <ListFilter size={12} data-id="element-2906" />
                      {q.scope === 'all_orders' ? 'All Orders' : `Specific Items (${q.itemIds?.length || 0})`}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2" data-id="element-2907">
                  <Toggle checked={q.active} onChange={active => updateQuestion(q.id, {
              active
            })} data-id="element-2908" />
                  <div className="w-px h-6 bg-gray-200 mx-1" data-id="element-2909" />
                  <button onClick={() => setEditingId(q.id)} className="p-2 text-gray-400 hover:text-orange-600 transition-colors rounded-lg hover:bg-orange-50" data-id="element-2910">
                    <Edit2 size={16} data-id="element-2911" />
                  </button>
                  <button onClick={() => deleteQuestion(q.id)} className="p-2 text-gray-400 hover:text-rose-600 transition-colors rounded-lg hover:bg-rose-50" data-id="element-2912">
                    <Trash2 size={16} data-id="element-2913" />
                  </button>
                </div>
              </div>
            </div>)}
        {filtered.length === 0 && !isAdding && <div className="text-center py-10 text-gray-500 text-sm" data-id="element-2914">
            No questions found.
          </div>}
      </div>
    </div>;
}
function QuestionEditor({
  initial,
  onSave,
  onCancel
}: {
  initial?: Question;
  onSave: (q: Omit<Question, 'id'>) => void;
  onCancel: () => void;
}) {
  const [question, setQuestion] = useState(initial?.question || '');
  const [helpText, setHelpText] = useState(initial?.helpText || '');
  const [type, setType] = useState<QuestionType>(initial?.type || 'text');
  const [options, setOptions] = useState<string[]>(initial?.options || ['']);
  const [required, setRequired] = useState(initial?.required ?? false);
  const [scope, setScope] = useState<QuestionScope>(initial?.scope || 'all_orders');
  const [itemIds, setItemIds] = useState<string[]>(initial?.itemIds || []);
  const handleSave = () => {
    if (!question.trim()) return;
    onSave({
      question: question.trim(),
      helpText: helpText.trim() || undefined,
      type,
      options: type !== 'text' ? options.filter(o => o.trim()) : undefined,
      required,
      scope,
      itemIds: scope === 'item_triggered' ? itemIds : undefined,
      active: initial?.active ?? true
    });
  };
  return <div className="bg-white border border-orange-200 shadow-sm rounded-xl p-5 space-y-5" data-id="element-2915">
      <div data-id="element-2916">
        <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5" data-id="element-2917">
          Question Prompt *
        </label>
        <input type="text" value={question} onChange={e => setQuestion(e.target.value)} placeholder="e.g. How would you like your steak cooked?" className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500" autoFocus data-id="element-2918" />
      </div>

      <div data-id="element-2919">
        <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5" data-id="element-2920">
          Help Text (Optional)
        </label>
        <input type="text" value={helpText} onChange={e => setHelpText(e.target.value)} placeholder="e.g. Please let us know any dietary restrictions." className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500" data-id="element-2921" />
      </div>

      <div className="grid grid-cols-2 gap-4" data-id="element-2922">
        <div data-id="element-2923">
          <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5" data-id="element-2924">
            Answer Type
          </label>
          <select value={type} onChange={e => setType(e.target.value as QuestionType)} className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500" data-id="element-2925">
            <option value="text" data-id="element-2926">Free Text</option>
            <option value="single-select" data-id="element-2927">Single Choice</option>
            <option value="multi-select" data-id="element-2928">Multiple Choice</option>
          </select>
        </div>
        <div data-id="element-2929">
          <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5" data-id="element-2930">
            Required?
          </label>
          <div className="flex items-center h-[38px]" data-id="element-2931">
            <Toggle checked={required} onChange={setRequired} data-id="element-2932" />
            <span className="ml-2 text-sm text-gray-600" data-id="element-2933">
              {required ? 'Must answer' : 'Optional'}
            </span>
          </div>
        </div>
      </div>

      {type !== 'text' && <div data-id="element-2934">
          <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5" data-id="element-2935">
            Choices
          </label>
          <div className="space-y-2" data-id="element-2936">
            {options.map((opt, i) => <div key={i} className="flex items-center gap-2" data-id="element-2937">
                <input type="text" value={opt} onChange={e => {
            const newOpts = [...options];
            newOpts[i] = e.target.value;
            setOptions(newOpts);
          }} placeholder={`Choice ${i + 1}`} className="flex-1 px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500" data-id="element-2938" />
                <button type="button" onClick={() => setOptions(options.filter((_, idx) => idx !== i))} className="p-2 text-gray-400 hover:text-rose-600 transition-colors" data-id="element-2939">
                  <Trash2 size={16} data-id="element-2940" />
                </button>
              </div>)}
            <button type="button" onClick={() => setOptions([...options, ''])} className="text-sm text-orange-600 font-semibold hover:text-orange-700 flex items-center gap-1" data-id="element-2941">
              <Plus size={14} data-id="element-2942" /> Add Choice
            </button>
          </div>
        </div>}

      <div data-id="element-2943">
        <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5" data-id="element-2944">
          When to ask
        </label>
        <div className="flex gap-2 mb-3" data-id="element-2945">
          <button type="button" onClick={() => setScope('all_orders')} className={`flex-1 py-2 text-sm font-semibold rounded-lg border-2 transition-colors ${scope === 'all_orders' ? 'border-orange-500 bg-orange-50 text-orange-700' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`} data-id="element-2946">
            Every Order
          </button>
          <button type="button" onClick={() => setScope('item_triggered')} className={`flex-1 py-2 text-sm font-semibold rounded-lg border-2 transition-colors ${scope === 'item_triggered' ? 'border-orange-500 bg-orange-50 text-orange-700' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`} data-id="element-2947">
            Specific Items
          </button>
        </div>

        {scope === 'item_triggered' && <div className="p-4 bg-gray-50 rounded-xl border border-gray-200" data-id="element-2948">
            <div className="text-xs font-bold text-gray-700 uppercase tracking-wider mb-3" data-id="element-2949">
              Trigger Items
            </div>
            <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto pr-2" data-id="element-2950">
              {catalogItems.map(item => {
            const selected = itemIds.includes(item.id);
            return <button key={item.id} type="button" onClick={() => {
              if (selected) {
                setItemIds(itemIds.filter(id => id !== item.id));
              } else {
                setItemIds([...itemIds, item.id]);
              }
            }} className={`flex items-center gap-2 p-2 rounded-lg border text-left transition-colors ${selected ? 'bg-white border-orange-500 shadow-sm' : 'bg-white border-gray-200 hover:border-gray-300'}`} data-id="element-2951">
                    <div className={`w-4 h-4 rounded-full border flex items-center justify-center flex-shrink-0 ${selected ? 'bg-orange-500 border-orange-500 text-white' : 'border-gray-300'}`} data-id="element-2952">
                      {selected && <CheckCircle2 size={12} data-id="element-2953" />}
                    </div>
                    <span className="text-sm font-medium text-gray-900 truncate" data-id="element-2954">
                      {item.name}
                    </span>
                  </button>;
          })}
            </div>
          </div>}
      </div>

      <div className="flex justify-end gap-3 pt-4 border-t border-gray-100" data-id="element-2955">
        <button onClick={onCancel} className="px-4 py-2 text-sm font-bold text-gray-600 hover:bg-gray-100 rounded-xl transition-colors" data-id="element-2956">
          Cancel
        </button>
        <button onClick={handleSave} disabled={!question.trim()} className="px-4 py-2 text-sm font-bold bg-orange-500 text-white rounded-xl hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-2957">
          Save Question
        </button>
      </div>
    </div>;
}